/**
 * TransitionPreview Component
 *
 * Shows a mini animated preview of a transition effect between two slide thumbnails.
 * Uses CSS animations to mimic GSAP transition effects.
 *
 * Features:
 * - Loops animation while active
 * - Falls back to colored gradients if thumbnails unavailable
 * - Supports all transition types from TransitionEngine
 */

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { TransitionConfig, TransitionType } from '@/types/videoManifest';

export interface TransitionPreviewProps {
  /** URL or gradient for the "from" slide */
  fromImage?: string;
  /** URL or gradient for the "to" slide */
  toImage?: string;
  /** Transition configuration */
  transition: TransitionConfig;
  /** Whether to play the animation */
  isActive: boolean;
  /** Size of the preview canvas */
  size?: { width: number; height: number };
  /** Additional class names */
  className?: string;
}

// Default gradient backgrounds for slides without thumbnails
const DEFAULT_FROM_BG = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
const DEFAULT_TO_BG = 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)';

/**
 * CSS animation keyframes for each transition type
 * These approximate the GSAP TransitionEngine animations
 */
const getTransitionStyles = (
  type: TransitionType,
  duration: number,
  easing: string
): { from: React.CSSProperties; to: React.CSSProperties; fromAnimation: string; toAnimation: string } => {
  const cssEasing = easing === 'linear' ? 'linear' :
    easing === 'expo.inOut' ? 'cubic-bezier(0.87, 0, 0.13, 1)' :
    easing === 'power3.inOut' ? 'cubic-bezier(0.65, 0, 0.35, 1)' :
    'cubic-bezier(0.45, 0, 0.55, 1)'; // power2.inOut default

  const animationBase = `${duration}s ${cssEasing} infinite`;

  switch (type) {
    case 'crossfade':
    case 'fade':
      return {
        from: {},
        to: {},
        fromAnimation: `crossfade-out ${animationBase}`,
        toAnimation: `crossfade-in ${animationBase}`,
      };

    case 'cut':
    case 'none':
      return {
        from: {},
        to: {},
        fromAnimation: `cut-out ${duration}s step-end infinite`,
        toAnimation: `cut-in ${duration}s step-end infinite`,
      };

    case 'fade-black':
      return {
        from: {},
        to: {},
        fromAnimation: `fade-black-out ${animationBase}`,
        toAnimation: `fade-black-in ${animationBase}`,
      };

    case 'slide-left':
      return {
        from: {},
        to: {},
        fromAnimation: `slide-left-out ${animationBase}`,
        toAnimation: `slide-left-in ${animationBase}`,
      };

    case 'slide-right':
      return {
        from: {},
        to: {},
        fromAnimation: `slide-right-out ${animationBase}`,
        toAnimation: `slide-right-in ${animationBase}`,
      };

    case 'slide-up':
      return {
        from: {},
        to: {},
        fromAnimation: `slide-up-out ${animationBase}`,
        toAnimation: `slide-up-in ${animationBase}`,
      };

    case 'slide-down':
      return {
        from: {},
        to: {},
        fromAnimation: `slide-down-out ${animationBase}`,
        toAnimation: `slide-down-in ${animationBase}`,
      };

    case 'wipe-left':
      return {
        from: {},
        to: {},
        fromAnimation: '',
        toAnimation: `wipe-left ${animationBase}`,
      };

    case 'wipe-right':
      return {
        from: {},
        to: {},
        fromAnimation: '',
        toAnimation: `wipe-right ${animationBase}`,
      };

    case 'circle-reveal':
      return {
        from: {},
        to: {},
        fromAnimation: '',
        toAnimation: `circle-reveal ${animationBase}`,
      };

    case 'zoom-in':
      return {
        from: {},
        to: {},
        fromAnimation: `zoom-in-out ${animationBase}`,
        toAnimation: `zoom-in-in ${animationBase}`,
      };

    case 'zoom-out':
      return {
        from: {},
        to: {},
        fromAnimation: `zoom-out-out ${animationBase}`,
        toAnimation: `zoom-out-in ${animationBase}`,
      };

    case 'blur-fade':
      return {
        from: {},
        to: {},
        fromAnimation: `blur-fade-out ${animationBase}`,
        toAnimation: `blur-fade-in ${animationBase}`,
      };

    // Content-aware transitions - simplified to cascade effect
    case 'content-exit':
    case 'stagger-cascade':
    case 'stagger-random':
      return {
        from: {},
        to: {},
        fromAnimation: `stagger-out ${animationBase}`,
        toAnimation: `stagger-in ${animationBase}`,
      };

    default:
      // Default to crossfade
      return {
        from: {},
        to: {},
        fromAnimation: `crossfade-out ${animationBase}`,
        toAnimation: `crossfade-in ${animationBase}`,
      };
  }
};

export const TransitionPreview: React.FC<TransitionPreviewProps> = ({
  fromImage,
  toImage,
  transition,
  isActive,
  size = { width: 160, height: 90 },
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [animationKey, setAnimationKey] = useState(0);

  // Reset animation when transition type changes
  useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [transition.type, transition.duration, transition.easing]);

  const { fromAnimation, toAnimation } = getTransitionStyles(
    transition.type,
    transition.duration,
    transition.easing || 'power2.inOut'
  );

  const fromBackground = fromImage ? `url(${fromImage})` : DEFAULT_FROM_BG;
  const toBackground = toImage ? `url(${toImage})` : DEFAULT_TO_BG;

  return (
    <>
      {/* CSS Keyframes - injected once */}
      <style>{`
        @keyframes crossfade-out {
          0%, 10% { opacity: 1; }
          50%, 60% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes crossfade-in {
          0%, 10% { opacity: 0; }
          50%, 60% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes cut-out {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes cut-in {
          0%, 49% { opacity: 0; }
          50%, 100% { opacity: 1; }
        }
        @keyframes fade-black-out {
          0%, 10% { opacity: 1; }
          25%, 75% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes fade-black-in {
          0%, 10% { opacity: 0; }
          50%, 60% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes slide-left-out {
          0%, 10% { transform: translateX(0); opacity: 1; }
          50%, 60% { transform: translateX(-100%); opacity: 1; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slide-left-in {
          0%, 10% { transform: translateX(100%); opacity: 1; }
          50%, 60% { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(100%); opacity: 1; }
        }
        @keyframes slide-right-out {
          0%, 10% { transform: translateX(0); opacity: 1; }
          50%, 60% { transform: translateX(100%); opacity: 1; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slide-right-in {
          0%, 10% { transform: translateX(-100%); opacity: 1; }
          50%, 60% { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(-100%); opacity: 1; }
        }
        @keyframes slide-up-out {
          0%, 10% { transform: translateY(0); opacity: 1; }
          50%, 60% { transform: translateY(-100%); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes slide-up-in {
          0%, 10% { transform: translateY(100%); opacity: 1; }
          50%, 60% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(100%); opacity: 1; }
        }
        @keyframes slide-down-out {
          0%, 10% { transform: translateY(0); opacity: 1; }
          50%, 60% { transform: translateY(100%); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes slide-down-in {
          0%, 10% { transform: translateY(-100%); opacity: 1; }
          50%, 60% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-100%); opacity: 1; }
        }
        @keyframes wipe-left {
          0%, 10% { clip-path: inset(0 0 0 100%); }
          50%, 60% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(0 0 0 100%); }
        }
        @keyframes wipe-right {
          0%, 10% { clip-path: inset(0 100% 0 0); }
          50%, 60% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(0 100% 0 0); }
        }
        @keyframes circle-reveal {
          0%, 10% { clip-path: circle(0% at center); }
          50%, 60% { clip-path: circle(75% at center); }
          100% { clip-path: circle(0% at center); }
        }
        @keyframes zoom-in-out {
          0%, 10% { transform: scale(1); opacity: 1; }
          50%, 60% { transform: scale(1.3); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes zoom-in-in {
          0%, 10% { transform: scale(0.7); opacity: 0; }
          50%, 60% { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.7); opacity: 0; }
        }
        @keyframes zoom-out-out {
          0%, 10% { transform: scale(1); opacity: 1; }
          50%, 60% { transform: scale(0.7); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes zoom-out-in {
          0%, 10% { transform: scale(1.3); opacity: 0; }
          50%, 60% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        @keyframes blur-fade-out {
          0%, 10% { filter: blur(0); opacity: 1; }
          50%, 60% { filter: blur(8px); opacity: 0; }
          100% { filter: blur(0); opacity: 1; }
        }
        @keyframes blur-fade-in {
          0%, 10% { filter: blur(8px); opacity: 0; }
          50%, 60% { filter: blur(0); opacity: 1; }
          100% { filter: blur(8px); opacity: 0; }
        }
        @keyframes stagger-out {
          0%, 10% { transform: translateY(0) scale(1); opacity: 1; }
          50%, 60% { transform: translateY(-30%) scale(0.8); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes stagger-in {
          0%, 10% { transform: translateY(30%) scale(0.8); opacity: 0; }
          50%, 60% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(30%) scale(0.8); opacity: 0; }
        }
      `}</style>

      <div
        ref={containerRef}
        className={cn(
          'relative overflow-hidden rounded-lg bg-zinc-900',
          className
        )}
        style={{ width: size.width, height: size.height }}
      >
        {/* Black background for fade-black transition */}
        {transition.type === 'fade-black' && (
          <div className="absolute inset-0 bg-black z-0" />
        )}

        {/* From slide */}
        <div
          key={`from-${animationKey}`}
          className="absolute inset-0 bg-cover bg-center z-10"
          style={{
            background: fromBackground,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: isActive ? fromAnimation : 'none',
            animationPlayState: isActive ? 'running' : 'paused',
          }}
        >
          {/* Slide number overlay */}
          <div className="absolute bottom-1 left-1 text-[8px] font-bold text-white/80 bg-black/30 px-1 rounded">
            1
          </div>
        </div>

        {/* To slide */}
        <div
          key={`to-${animationKey}`}
          className="absolute inset-0 bg-cover bg-center z-20"
          style={{
            background: toBackground,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: isActive ? toAnimation : 'none',
            animationPlayState: isActive ? 'running' : 'paused',
            opacity: isActive ? undefined : 0,
          }}
        >
          {/* Slide number overlay */}
          <div className="absolute bottom-1 left-1 text-[8px] font-bold text-white/80 bg-black/30 px-1 rounded">
            2
          </div>
        </div>

        {/* Transition type label */}
        <div className="absolute top-1 right-1 text-[8px] font-medium text-white/60 bg-black/40 px-1.5 py-0.5 rounded backdrop-blur-sm z-30">
          {transition.type}
        </div>
      </div>
    </>
  );
};

export default TransitionPreview;
